import { useMemo, useState } from 'react';
import { Bot, Activity } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardBody, Skeleton, SegmentedControl } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, EmptyState } from '@vnetwork/ui/data';
import { Chart, PieChart } from '@vnetwork/ui/charts';
import { useBotMgmtAnalytics } from '@/api/manticore';
import { toneColor } from '../../shared';
import { asBars, findTimeSeries, BarCard } from '../../_shared/analytics';
import styles from './BotMgmtAnalyticsTab.module.css';

/**
 * Bot Management analytics — Analytics-page archetype. Binds to the REAL
 * `useBotMgmtAnalytics` response (CDNBotMgmtAnalyticsResponse from the fan-out):
 * `overview.{total,unique_ips,hard_block,js_pow,challenge_429,challenge_html}`,
 * a `timeline` time-series, a `decision_breakdown`, and `top_blocked_ips |
 * top_countries | top_user_agents`. The backend ships a real `timeline`, so the
 * activity chart renders from live data (no mock).
 */

const RANGES = [
  { value: '1', label: '1H' },
  { value: '6', label: '6H' },
  { value: '24', label: '24H' },
  { value: '168', label: '7D' },
  { value: '720', label: '30D' },
];

const num = (o: Record<string, unknown> | null | undefined, k: string) => Number(o?.[k] ?? 0);

interface Decision {
  name: string;
  value: number;
  color?: string;
}

// Friendly label + colour per decision key (BE ships raw keys like `challenge_429`).
const DECISION_META: Record<string, { label: string; color: string }> = {
  challenge_429: { label: 'Challenge 429', color: toneColor('warning') },
  challenge_html: { label: 'Challenge HTML', color: 'var(--vn-accent-solid)' },
  js_pow: { label: 'JS PoW', color: toneColor('info') },
  hard_block: { label: 'Hard block', color: toneColor('danger') },
  allow: { label: 'Allowed', color: toneColor('success') },
  allowed: { label: 'Allowed', color: toneColor('success') },
};

/** Decision donut from the real `decision_breakdown`, else derived from overview. */
function decisionSlices(breakdown: unknown, overview: Record<string, unknown> | null): Decision[] {
  if (Array.isArray(breakdown) && breakdown.length > 0) {
    return (breakdown as Record<string, unknown>[])
      .map((r) => {
        const key = String(r.decision ?? r.name ?? r.action ?? r.category ?? r.label ?? '');
        const meta = DECISION_META[key];
        // BE rows carry the count under `events` (not count/value).
        return { name: meta?.label ?? key ?? '—', value: Number(r.count ?? r.value ?? r.events ?? 0), color: meta?.color };
      })
      .filter((d) => d.value > 0);
  }
  return [
    { name: 'Hard block', value: num(overview, 'hard_block'), color: toneColor('danger') },
    { name: 'JS PoW', value: num(overview, 'js_pow'), color: toneColor('info') },
    { name: 'Challenge 429', value: num(overview, 'challenge_429'), color: toneColor('warning') },
    { name: 'Challenge HTML', value: num(overview, 'challenge_html'), color: 'var(--vn-accent-solid)' },
  ].filter((d) => d.value > 0);
}

export function BotMgmtAnalyticsTab({ domain, domainId }: { domain: string; domainId: string }) {
  const [hours, setHours] = useState('24');
  const { data, isLoading } = useBotMgmtAnalytics(domainId, `hours=${hours}`);
  const overview = (data?.overview as Record<string, unknown>) ?? null;

  const decisions = useMemo(() => decisionSlices(data?.decision_breakdown, overview), [data, overview]);
  const decided = decisions.reduce((s, d) => s + d.value, 0);
  const timeline = useMemo(() => findTimeSeries(data), [data]);

  return (
    <Section
      title="Bot Management Analytics"
      description={`Bot decisions for ${domain}.`}
      actions={<SegmentedControl size="sm" items={RANGES} value={hours} onValueChange={setHours} aria-label="Time range" />}
    >
      {isLoading ? (
        <Skeleton style={{ height: 240 }} />
      ) : !data ? (
        <EmptyState icon={<Bot size={20} />} title="No bot analytics yet" description="Decisions will appear here once bot management runs." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Stat header + decision donut */}
          <div className={styles.analyticsGrid}>
            <Card flat>
              <CardBody>
                <StatGroup columns={2}>
                  <Stat label="Total checks" value={num(overview, 'total').toLocaleString()} />
                  <Stat label="Unique IPs" value={num(overview, 'unique_ips').toLocaleString()} />
                  <Stat label="Hard blocks" value={num(overview, 'hard_block').toLocaleString()} dotColor={toneColor('danger')} />
                  <Stat label="JS PoW" value={num(overview, 'js_pow').toLocaleString()} dotColor={toneColor('info')} />
                </StatGroup>
              </CardBody>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Decision breakdown</CardTitle>
              </CardHeader>
              <CardBody>
                {decisions.length === 0 ? (
                  <EmptyState size="sm" icon={<Activity size={20} />} title="No decisions" description="No decisions recorded in the selected range." />
                ) : (
                  <PieChart
                    donut
                    height={180}
                    data={decisions}
                    showLegend
                    centerLabel={
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 'var(--vn-font-size-xl)', fontWeight: 'var(--vn-font-weight-semibold)' }}>{decided.toLocaleString()}</div>
                        <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>actioned</div>
                      </div>
                    }
                  />
                )}
              </CardBody>
            </Card>
          </div>

          {/* Real time-series from the `timeline` field */}
          {timeline.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Bot activity over time</CardTitle>
              </CardHeader>
              <CardBody>
                <Chart
                  type="area"
                  data={timeline}
                  xKey="t"
                  series={[{ key: 'v', label: 'Bot events', color: 'var(--vn-color-blue-400)' }]}
                  height={200}
                  showGrid
                />
              </CardBody>
            </Card>
          )}

          {/* Ranked breakdowns */}
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <BarCard title="Top blocked IPs" items={asBars(data.top_blocked_ips, { labelKeys: ['client_ip', 'ip'] })} />
            <BarCard title="Top countries" items={asBars(data.top_countries, { labelKeys: ['country_code', 'country'] })} />
            <BarCard title="Top user agents" items={asBars(data.top_user_agents, { labelKeys: ['user_agent'] })} />
          </div>
        </div>
      )}
    </Section>
  );
}
